/**
 * GodModeContext — Manages inspection state for the God Mode system.
 *
 * Tracks which entity is being hovered (shows tooltip) and which is
 * selected (shows detail panel). Separate from AuthContext because
 * this is UI state, not identity state.
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback } from 'react';

const GodModeContext = createContext(null);

export function GodModeProvider({ children }) {
  // Hovered entity: { type, entity, rect } or null
  const [hoveredEntity, setHoveredEntity] = useState(null);

  // Selected entity for the detail panel: { type, entity } or null
  const [selectedEntity, setSelectedEntity] = useState(null);

  // Which side the panel prefers
  const [panelSide, setPanelSide] = useState('right');

  const setHovered = useCallback((type, entity, rect) => {
    setHoveredEntity(type && entity ? { type, entity, rect } : null);
  }, []);

  const setSelected = useCallback((type, entity, preferLeft = false) => {
    setSelectedEntity(type && entity ? { type, entity } : null);
    setPanelSide(preferLeft ? 'left' : 'right');
  }, []);

  const closePanel = useCallback(() => {
    setSelectedEntity(null);
  }, []);

  const clearHover = useCallback(() => {
    setHoveredEntity(null);
  }, []);

  return (
    <GodModeContext.Provider value={{
      hoveredEntity,
      selectedEntity,
      panelSide,
      setHovered,
      setSelected,
      closePanel,
      clearHover,
    }}>
      {children}
    </GodModeContext.Provider>
  );
}

export function useGodMode() {
  const context = useContext(GodModeContext);
  if (!context) throw new Error('useGodMode must be used within GodModeProvider');
  return context;
}
